import React, { useEffect, useMemo, useState } from "react";
import {
    Row, Col, Card, CardBody, Label, Input, Button, FormGroup, Badge
} from "reactstrap";
import api from "../../api";
import Select from '../../Components/Common/ThemedSelect'; // Round THEME-2: follows the theme
import DataTable from 'react-data-table-component';

const defaultLine = {
    description: "",
    productGroup: "",
    dept: "",
    accountNo: "",
    quantity: 1,
    unitPrice: 0,
    taxPct: 0,
    discountPct: 0,
    uom: "Each",
    uomOther: "",
    assetPurchase: false,
    rdExpense: false,
};

function number(v) { return isNaN(parseFloat(v)) ? 0 : parseFloat(v); }

function calcExtendedCost(l) {
    const qty = number(l.quantity);
    const price = number(l.unitPrice);
    const disc = number(l.discountPct) / 100;
    const tax = number(l.taxPct) / 100;
    const base = qty * price;
    const afterDiscount = base * (1 - disc);
    return +(afterDiscount * (1 + tax)).toFixed(2);
}

export default function LineItems({ lines, setLines }) {
    const [line, setLine] = useState(defaultLine);
    const [dontClear, setDontClear] = useState(false);
    const [editIndex, setEditIndex] = useState(null); // null = adding new, number = editing existing
    const [lineErrors, setLineErrors] = useState([]);   // Owner 2026-10-14: what still has to be filled in

    const [productGroups, setProductGroups] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [accountNumbers, setAccountNumbers] = useState([]);

    useEffect(() => {
        api.get("Epo/account-numbers")  /* R43: case-normalized */.then(res => setAccountNumbers(res.data || [])).catch(() => setAccountNumbers([]));
    }, []);
    useEffect(() => {
        api.get("Epo/product-groups")  /* R43: case-normalized */.then(res => setProductGroups(res.data)).catch(() => setProductGroups([]));
    }, []);
    useEffect(() => {
        // Owner fix (2026-08-04) — "/Department" was the LEGACY server's OData
        // feed (leading slash + prod base), which is why the list emptied once
        // localhost stopped talking to production. Departments now come from
        // the modern API (legacy-parity query) — localhost works standalone.
        api.get("Epo/departments").then(res => setDepartments(res.data || [])).catch(() => setDepartments([]));
    }, []);

    const extCost = useMemo(() => calcExtendedCost(line), [line]);

    const onField = (name) => (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setLine((prev) => ({ ...prev, [name]: value }));
    };

    // Owner 2026-10-14: a line is saved only when it is complete, as the legacy form
    // required (EpoNew.vb: description, product group, department, account, quantity,
    // and a unit of measure when "Other" is picked). The server refuses the same.
    const missingOnLine = (l) => {
        const out = [];
        if (!String(l.description || "").trim()) out.push("Product / Service description");
        if (!String(l.productGroup || "").trim()) out.push("Product Group");
        if (!String(l.dept || "").trim()) out.push("Department");
        if (!String(l.accountNo || "").trim()) out.push("Account No.");
        if (!(number(l.quantity) > 0)) out.push("Quantity (more than zero)");
        if (isNaN(parseFloat(l.unitPrice)) || number(l.unitPrice) < 0) out.push("Unit Price (zero or more)");
        if (l.uom === "Other" && !String(l.uomOther || "").trim()) out.push("Unit of measure");
        return out;
    };

    const saveLine = () => {
        const missing = missingOnLine(line);
        if (missing.length > 0) {
            setLineErrors(missing);
            document.getElementById("line-entry-form")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
            return;
        }
        setLineErrors([]);
        if (number(line.unitPrice) === 0
            && !window.confirm("This line is $0.00. Are you sure you want to save it? Click OK to keep it or Cancel to change it.")) {
            return;
        }
        const toSave = {
            ...line,
            extendedCost: calcExtendedCost(line),
            uomFinal: line.uom === "Other" ? line.uomOther : "Each",
        };

        if (editIndex !== null) {
            // Update existing line
            // Go-live parity (2026-09-24): EPO#N11 — keep the stored lineNum (and
            // receive state) of the line being edited, so the save updates THAT line.
            setLines(prev => prev.map((l, i) => i === editIndex ? { ...l, ...toSave } : l));
            setEditIndex(null);
        } else {
            // Add new line
            setLines(prev => [...prev, toSave]);
        }

        if (!dontClear) setLine(defaultLine);
    };

    const cancelEdit = () => {
        setEditIndex(null);
        setLine(defaultLine);
        setLineErrors([]);
    };

    const editLine = (idx) => {
        const l = lines[idx];
        setEditIndex(idx);
        setLine({
            description: l.description,
            productGroup: l.productGroup,
            dept: l.dept,
            accountNo: l.accountNo,
            quantity: l.quantity,
            unitPrice: l.unitPrice,
            taxPct: l.taxPct,
            discountPct: l.discountPct,
            uom: l.uom || (l.uomFinal === "Each" ? "Each" : "Other"),
            uomOther: l.uom === "Other" ? l.uomFinal : "",
            assetPurchase: l.assetPurchase,
            rdExpense: l.rdExpense,
        });
        // Scroll to form
        document.getElementById("line-entry-form")?.scrollIntoView({ behavior: "smooth" });
    };

    const deleteLine = (idx) => {
        setLines(prev => prev.filter((_, i) => i !== idx));
        if (editIndex === idx) {
            setEditIndex(null);
            setLine(defaultLine);
        }
    };

    const productGroupOptions = productGroups.map(pg => ({ value: pg.id, label: pg.name }));
    const departmentOptions = departments.map(d => ({ value: d.jcDept1, label: `${d.jcDept1} - ${d.description}` }));
    const accountOptions = accountNumbers.map(a => ({ value: a.id, label: a.name }));

    // DataTable columns
    const columns = [
        {
            name: '#',
            selector: (_, i) => i + 1,
            width: '50px',
            center: true,
        },
        {
            name: 'Description',
            selector: row => row.description,
            wrap: true,
            minWidth: '160px',
        },
        {
            name: 'Prod',
            selector: row => row.productGroup,
            width: '70px',
            center: true,
        },
        {
            name: 'Dept',
            selector: row => row.dept,
            width: '70px',
            center: true,
        },
        {
            name: 'Acct',
            selector: row => row.accountNo,
            width: '80px',
            center: true,
        },
        {
            name: 'Qty',
            selector: row => number(row.quantity),
            width: '60px',
            right: true,
        },
        {
            name: 'Unit Price',
            selector: row => `$${number(row.unitPrice).toFixed(2)}`,
            width: '90px',
            right: true,
        },
        {
            name: 'Tax %',
            selector: row => number(row.taxPct).toFixed(2),
            width: '70px',
            right: true,
        },
        {
            name: 'Disc %',
            selector: row => number(row.discountPct).toFixed(2),
            width: '70px',
            right: true,
        },
        {
            name: 'Extended Cost',
            selector: row => `$${number(row.extendedCost).toFixed(2)}`,
            width: '110px',
            right: true,
            cell: row => (
                <strong className="text-success">${number(row.extendedCost).toFixed(2)}</strong>
            ),
        },
        {
            name: 'UOM',
            selector: row => row.uomFinal,
            width: '70px',
            center: true,
        },
        {
            name: 'Asset',
            selector: row => row.assetPurchase,
            width: '65px',
            center: true,
            cell: row => row.assetPurchase ? <Badge color="warning" pill>Yes</Badge> : null,
        },
        {
            name: 'R&D',
            selector: row => row.rdExpense,
            width: '60px',
            center: true,
            cell: row => row.rdExpense ? <Badge color="info" pill>Yes</Badge> : null,
        },
        {
            // R32 — read-only "Rcvd" column. Set by Shipping via the receive
            // page (/epo/{n}/receive); Finance just sees the state, doesn't toggle
            // it. Mirrors the "Rcvd" checkbox in legacy's Review EPO Line Items grid.
            name: 'Rcvd',
            selector: row => !!row.recvd,
            width: '70px',
            center: true,
            cell: row => {
                if (row.recvd) {
                    const dt = row.dateTimeRec
                        ? new Date(row.dateTimeRec).toLocaleDateString()
                        : null;
                    const tip = [row.recvdBy && `by ${row.recvdBy}`, dt].filter(Boolean).join(" ");
                    return <Badge color="success" pill title={tip || undefined}><i className="ri-check-line" /></Badge>;
                }
                if (Number(row.qtyRec) > 0) {
                    return <Badge color="warning" pill title={`${row.qtyRec} received`}>Part</Badge>;
                }
                return <span className="text-muted">—</span>;
            },
        },
        {
            name: 'Actions',
            width: '130px',
            center: true,
            // Owner 2026-10-14: a line Finance has already paid cannot be changed or
            // removed (add a new line for the new charge).
            cell: (row, index) => row.paid ? (
                <Badge color="success" pill title="Paid by Finance. Add a new line for a new charge.">Paid</Badge>
            ) : (
                <div className="d-flex gap-1">
                    <Button
                        size="sm"
                        color="outline-primary"
                        onClick={() => editLine(index)}
                        title="Edit"
                    >
                        <i className="ri-pencil-line" />
                    </Button>
                    <Button
                        size="sm"
                        color="outline-danger"
                        onClick={() => deleteLine(index)}
                        title="Remove"
                    >
                        <i className="ri-delete-bin-line" />
                    </Button>
                </div>
            ),
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: 'var(--ssw-surface-3, #f8f9fa)',
                borderBottom: '2px solid var(--ssw-line, #dee2e6)',
                fontWeight: '600',
                fontSize: '13px',
            },
        },
        rows: {
            style: {
                fontSize: '13px',
                '&:nth-of-type(odd)': {
                    backgroundColor: 'var(--ssw-surface-3, #fafafa)',
                },
                '&:hover': {
                    backgroundColor: 'var(--ssw-tint-gray, #f0f4ff)',
                    cursor: 'pointer',
                },
            },
            highlightOnHoverStyle: {
                backgroundColor: 'var(--ssw-tint-blue, #e8efff)',
            },
        },
        cells: {
            style: {
                paddingTop: '8px',
                paddingBottom: '8px',
            },
        },
    };

    const conditionalRowStyles = [
        {
            when: (_, index) => index === editIndex,
            style: {
                backgroundColor: 'var(--ssw-tint-yellow, #fff8e1)',
                borderLeft: '3px solid #ffc107',
            },
        },
    ];

    return (
        <Card>
            <CardBody>
                {/* DataTable */}
                <DataTable
                    columns={columns}
                    data={lines}
                    customStyles={customStyles}
                    conditionalRowStyles={conditionalRowStyles}
                    noDataComponent={
                        <div className="py-4 text-muted text-center">
                            <i className="ri-inbox-line fs-3 d-block mb-1" />
                            No line items yet. Fill in the form below and click <strong>Save Line</strong>.
                        </div>
                    }
                    dense
                    highlightOnHover
                    responsive
                />

                <hr />

                {/* Entry / Edit Form */}
                <div id="line-entry-form">
                    <h6 className="mb-3 fw-semibold">
                        {editIndex !== null
                            ? <><i className="ri-pencil-line me-1 text-warning" /> Editing Line #{editIndex + 1}</>
                            : <><i className="ri-add-circle-line me-1 text-primary" /> Add New Line Item</>
                        }
                    </h6>

                    <Row className="g-3">
                        <Col lg={6}>
                            <Label className="form-label">Product / Service</Label>
                            <Input
                                value={line.description}
                                onChange={onField("description")}
                                placeholder="Describe the product/service"
                            />
                        </Col>

                        <Col lg={6}>
                            <Label className="form-label">Product Group</Label>
                            <Select
                                options={productGroupOptions}
                                value={productGroupOptions.find(opt => opt.value === line.productGroup) || null}
                                onChange={(opt) => setLine(prev => ({ ...prev, productGroup: opt?.value || "" }))}
                                placeholder="Select group"
                                isClearable
                            />
                        </Col>

                        <Col lg={6}>
                            <Label className="form-label">Department</Label>
                            <Select
                                options={departmentOptions}
                                value={departmentOptions.find(opt => opt.value === line.dept) || null}
                                onChange={(opt) => setLine(prev => ({ ...prev, dept: opt?.value || "" }))}
                                placeholder="Select department"
                                isClearable
                            />
                        </Col>

                        <Col lg={6}>
                            <Label className="form-label">Account No.</Label>
                            <Select
                                options={accountOptions}
                                value={accountOptions.find(opt => opt.value === line.accountNo) || null}
                                onChange={(opt) => setLine(prev => ({ ...prev, accountNo: opt?.value || "" }))}
                                placeholder="Select account number"
                                isClearable
                            />
                        </Col>

                        <Col lg={3}>
                            <Label className="form-label">Quantity</Label>
                            <Input type="number" min="0" value={line.quantity} onChange={onField("quantity")} />
                        </Col>

                        <Col lg={3}>
                            <Label className="form-label">Unit Price</Label>
                            <Input type="number" step="0.01" min="0" value={line.unitPrice} onChange={onField("unitPrice")} />
                        </Col>

                        <Col lg={3}>
                            <Label className="form-label">Tax %</Label>
                            {/* Owner 2026-10-12: the legacy pick list (0, 6.625, 7.0) is offered as
                                suggestions; any other rate can still be typed. */}
                            <Input type="number" step="0.001" min="0" list="epo-tax-pct-list" value={line.taxPct} onChange={onField("taxPct")} />
                            <datalist id="epo-tax-pct-list">
                                <option value="0" />
                                <option value="6.625" />
                                <option value="7.0" />
                            </datalist>
                        </Col>

                        <Col lg={3}>
                            <Label className="form-label">Discount %</Label>
                            <Input type="number" step="0.01" min="0" value={line.discountPct} onChange={onField("discountPct")} />
                        </Col>

                        <Col lg={12}>
                            <Label className="form-label d-block">Unit of Measurement (UOM)</Label>
                            <div className="d-flex align-items-center gap-3">
                                <FormGroup check className="mb-0">
                                    <Input type="radio" id="uom-each" name="uom" checked={line.uom === "Each"} onChange={() => setLine(p => ({ ...p, uom: "Each" }))} />
                                    <Label htmlFor="uom-each" check className="ms-1">Each</Label>
                                </FormGroup>
                                <FormGroup check className="mb-0">
                                    <Input type="radio" id="uom-other" name="uom" checked={line.uom === "Other"} onChange={() => setLine(p => ({ ...p, uom: "Other" }))} />
                                    <Label htmlFor="uom-other" check className="ms-1">Other</Label>
                                </FormGroup>
                                {line.uom === "Other" && (
                                    <Input className="ms-2" style={{ maxWidth: 240 }} placeholder="Enter UOM (e.g., box, pack)" value={line.uomOther} onChange={onField("uomOther")} />
                                )}
                            </div>
                        </Col>

                        <Col lg={12}>
                            <div className="d-flex align-items-center gap-4 mt-1">
                                <FormGroup check className="mb-0">
                                    <Input type="checkbox" id="assetPurchase" checked={line.assetPurchase} onChange={onField("assetPurchase")} />
                                    <Label htmlFor="assetPurchase" check className="ms-1">Asset Purchase</Label>
                                </FormGroup>
                                <FormGroup check className="mb-0">
                                    <Input type="checkbox" id="rdExpense" checked={line.rdExpense} onChange={onField("rdExpense")} />
                                    <Label htmlFor="rdExpense" check className="ms-1">R&amp;D Expense</Label>
                                </FormGroup>
                                <span className="text-muted ms-auto">
                                    <strong>Extended Cost of this line:</strong> ${extCost.toFixed(2)}
                                </span>
                            </div>
                        </Col>

                        {lineErrors.length > 0 && (
                            <Col lg={12}>
                                <div className="alert alert-danger py-2 px-3 mb-0" role="alert" style={{ fontSize: 13 }}>
                                    <strong>Fill in before saving this line:</strong> {lineErrors.join(", ")}.
                                </div>
                            </Col>
                        )}

                        <Col lg={12} className="d-flex align-items-center gap-3 mt-3">
                            <FormGroup check className="mb-0">
                                <Input type="checkbox" id="dontClear" checked={dontClear} onChange={(e) => setDontClear(e.target.checked)} />
                                <Label htmlFor="dontClear" check className="ms-1">Don't Clear Fields</Label>
                            </FormGroup>

                            {editIndex !== null && (
                                <Button color="secondary" outline onClick={cancelEdit}>
                                    Cancel Edit
                                </Button>
                            )}

                            <Button color={editIndex !== null ? "warning" : "primary"} onClick={saveLine}>
                                {editIndex !== null ? "Update Line" : "Save Line"}
                            </Button>
                        </Col>
                    </Row>
                </div>
            </CardBody>
        </Card>
    );
}